import Vue from 'vue';
import VueRouter from 'vue-router';
import { authRequired, workspaceAccessRequired, onboardingRequired } from './guards';

/* Containers */
const AuthContainer = () => import('@/containers/Auth.vue');
const OnBoardingContainer = () => import('@/containers/OnBoarding.vue');
const DashboardContainer = () => import('@/containers/Dashboard.vue');

/* Authentication */
const Login = () => import('@/views/Authentication/Login.vue');
const Register = () => import('@/views/Authentication/Register.vue');
const ResetPassword = () => import('@/views/Authentication/ResetPassword.vue');

/* OnBoarding */
const Welcome = () => import('@/views/OnBoarding/Welcome.vue');

/* Dashboard */
const Dashboard = () => import('@/views/Dashboard/Dashboard/Dashboard.vue');
const LandingPages = () => import('@/views/Dashboard/LandingPages/LandingPages.vue');

const WizardType = () => import('@/views/Dashboard/Wizard/Type/Type.vue');
const WizardTemplate = () => import('@/views/Dashboard/Wizard/Template/Template.vue');
const WizardZip = () => import('@/views/Dashboard/Wizard/Zip/Zip.vue');
const WizardUrl = () => import('@/views/Dashboard/Wizard/Url/Url.vue');
const WizardImport = () => import('@/views/Dashboard/Wizard/Import/Import.vue');
const Websites = () => import('@/views/Dashboard/Websites/Websites.vue');
const Website = () => import('@/views/Dashboard/Website/Website.vue');
const Folder = () => import('@/views/Dashboard/Folder/Folder.vue');
const Lander = () => import('@/views/Dashboard/Lander/Lander.vue');
const Templates = () => import('@/views/Dashboard/Templates/Templates.vue');
const Domains = () => import('@/views/Dashboard/Domains/Domains.vue');
const Leads = () => import('@/views/Dashboard/Leads/Leads.vue');
const Reporting = () => import('@/views/Dashboard/Reporting/Reporting.vue');
const Settings = () => import('@/views/Dashboard/Settings/Settings.vue');
const Account = () => import('@/views/Dashboard/Account/Account.vue');
const Profile = () => import('@/views/User/Profile/Profile.vue');
const NoAccess = () => import('@/views/Dashboard/NoAccess/NoAccess.vue');

const Editor = () => import('@/views/Dashboard/Editor/Editor.vue');

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'Root',
    redirect: { name: 'Dashboard' },
  },
  {
    path: '/auth',
    name: 'AuthContainer',
    component: AuthContainer,
    redirect: { name: 'Login' },
    meta: { authRequired: true },
    children: [
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: 'register',
        name: 'Register',
        component: Register,
      },
      {
        path: 'reset-password',
        name: 'ResetPassword',
        component: ResetPassword,
      },
    ],
  },
  {
    path: '/onboarding',
    name: 'OnBoardingContainer',
    component: OnBoardingContainer,
    redirect: { name: 'Welcome' },
    meta: { onboardingRequired: true },
    children: [
      {
        path: 'welcome',
        name: 'Welcome',
        component: Welcome,
      },
    ],
  },
  {
    path: '/:workspaceId',
    name: 'DashboardContainer',
    redirect: { name: 'Dashboard' },
    component: DashboardContainer,
    meta: { workspaceAccessRequired: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
      },
      {
        path: 'landing-pages',
        name: 'LandingPages',
        component: LandingPages,
      },
      {
        path: 'landing-pages/create',
        name: 'WizardType',
        component: WizardType,
      },
      {
        path: 'landing-pages/create/template',
        name: 'WizardTemplate',
        component: WizardTemplate,
      },
      {
        path: 'landing-pages/create/zip',
        name: 'WizardZip',
        component: WizardZip,
      },
      {
        path: 'landing-pages/create/url',
        name: 'WizardUrl',
        component: WizardUrl,
      },
      {
        path: 'landing-pages/create/adplexity',
        name: 'WizardAdplexity',
        redirect: { name: 'WizardImport' },
      },
      {
        path: 'landing-pages/create/import',
        name: 'WizardImport',
        component: WizardImport,
      },
      {
        path: 'wizard',
        name: 'Wizard',
        redirect: { name: 'WizardImport' },
      },
      {
        path: 'landing-pages/folder/:id',
        name: 'Folder',
        component: Folder,
      },
      {
        path: 'landing-pages/:id/:tab?',
        name: 'Lander',
        component: Lander,
      },
      {
        path: 'websites',
        name: 'Websites',
        component: Websites,
      },
      {
        path: 'websites/create/template',
        name: 'WebsitesWizardTemplate',
        component: WizardTemplate,
      },
      {
        path: 'websites/:id/:tab?',
        name: 'Website',
        component: Website,
      },
      {
        path: 'templates',
        name: 'Templates',
        component: Templates,
      },
      {
        path: 'domains',
        name: 'Domains',
        component: Domains,
      },
      {
        path: 'leads',
        name: 'Leads',
        component: Leads,
      },
      {
        path: 'reporting',
        name: 'Reporting',
        component: Reporting,
      },
      {
        path: 'account-profile',
        name: 'Profile',
        component: Profile,
      },
      {
        path: 'settings/:tab?',
        name: 'Settings',
        component: Settings,
        meta: { scopeKey: 'organization' }
      },
      {
        path: 'account/:tab?',
        name: 'Account',
        component: Account,
        meta: { scopeKey: 'organization' }
      },
      {
        path: 'no-access',
        name: 'NoAccess',
        component: NoAccess,
      },
    ],
  },
  {
    path: '/:workspaceId/editor/:type?/:id',
    name: 'Editor',
    component: Editor,
    meta: { workspaceAccessRequired: true },
  },
  {
    path: '/*',
    name: 'Page404',
    redirect: { name: 'Root' },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  // Optional: analytics tracking
  // if ((from && to && from.name !== to.name) || !from) {
  //   mp.track(mpConfig.PAGE_VIEW, { page: camelCaseToSpaces(to.name).trim(), ...to.query });
  // }
  if (to.name === from.name && to.params.workspaceId === from.params.workspaceId) return next();
  if (to.matched.some((record) => record.meta.workspaceAccessRequired)) return workspaceAccessRequired(to, from, next);
  if (to.matched.some((record) => record.meta.authRequired)) return authRequired(to, from, next);
  if (to.matched.some((record) => record.meta.onboardingRequired)) return onboardingRequired(to, from, next);
  return next();
});

export default router;
